import { invokeFunction } from '../api'
import { toApiRequestError } from '../errors'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type BatchStatus = 'pending' | 'processing' | 'completed' | 'failed'
export type RuleResult = 'pass' | 'fail' | 'incomplete' | 'na'
export type ScopeLevel = 'limited' | 'pattern' | 'widespread'
export type ChartScore = 1 | 2 | 3

export interface AuditBatch {
  id: string
  facility_id: string
  batch_status: BatchStatus
  file_count: number
  processed_count: number
  intake_path: string
  score_distribution: Record<string, number> | null
  scope_classifications: Record<string, ScopeLevel> | null
  created_at: string
  completed_at: string | null
}

export interface ChartRecord {
  id: string
  chart_token: string
  procedure_type: string | null
  procedure_year: number | null
  intake_path: string
  extraction_status: string
  chart_score: ChartScore | null
  created_at: string
}

export interface ChartFinding {
  rule_id: string
  element_name: string
  result: RuleResult
  finding_statement: string | null
  regulatory_citation: string
  confidence_score: number | null
  timeliness_gap: string | null
  corrective_action_prompt: string | null
}

export interface ChartReport {
  chart_token: string
  procedure_type: string | null
  procedure_year: number | null
  chart_score: ChartScore | null
  findings: ChartFinding[]
}

export interface BatchReport {
  batch_id: string
  facility_id: string
  batch_status: BatchStatus
  audit_date: string
  sample_size: number
  score_distribution: Record<string, number> | null
  scope_classifications: Record<string, ScopeLevel> | null
  charts: ChartReport[]
}

export interface BatchSummary {
  batch_id: string
  facility_id: string
  audit_date: string
  sample_size: number
  score_distribution: Record<string, number> | null
  scope_classifications: Record<string, ScopeLevel> | null
  rule_aggregates: Record<string, { pass: number; fail: number; incomplete: number; na: number }>
  pip_recommendations: Array<{ rule_id: string; scope: string; standard: string }>
  trend: { prior_batch_id?: string; prior_date?: string; prior_score_distribution?: Record<string, number>; message?: string }
}

export interface RuleConfigEntry {
  rule_id: string
  config: Record<string, unknown>
  updated_at?: string
}

export interface RuleDefault {
  rule_id: string
  element_name: string
}

// ---------------------------------------------------------------------------
// Type guards
// ---------------------------------------------------------------------------

function asRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null
}

function toBatch(v: unknown): AuditBatch {
  if (!asRecord(v)) throw new Error('Malformed batch')
  return {
    id: String(v.id ?? ''),
    facility_id: String(v.facility_id ?? ''),
    batch_status: (v.batch_status as BatchStatus) ?? 'pending',
    file_count: Number(v.file_count ?? 0),
    processed_count: Number(v.processed_count ?? 0),
    intake_path: String(v.intake_path ?? 'pdf'),
    score_distribution: (v.score_distribution as Record<string, number>) ?? null,
    scope_classifications: (v.scope_classifications as Record<string, ScopeLevel>) ?? null,
    created_at: String(v.created_at ?? ''),
    completed_at: typeof v.completed_at === 'string' ? v.completed_at : null,
  }
}

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

export async function createBatch(facilityId: string, fileCount: number, intakePath = 'pdf'): Promise<AuditBatch> {
  const { data, error } = await invokeFunction<{ batch: unknown }>('chart-audit', {
    method: 'POST',
    path: '/batches',
    body: { facility_id: facilityId, file_count: fileCount, intake_path: intakePath },
  })
  if (error) throw toApiRequestError(error, 'Failed to create audit batch')
  return toBatch((data as Record<string, unknown>)?.batch)
}

export async function listBatches(facilityId?: string): Promise<AuditBatch[]> {
  const query = facilityId ? `?facility_id=${facilityId}` : ''
  const { data, error } = await invokeFunction<{ batches: unknown[] }>('chart-audit', {
    method: 'GET',
    path: `/batches${query}`,
  })
  if (error) throw toApiRequestError(error, 'Failed to list audit batches')
  return ((data as Record<string, unknown>)?.batches as unknown[] ?? []).map(toBatch)
}

export async function getBatch(batchId: string): Promise<{ batch: AuditBatch; records: ChartRecord[] }> {
  const { data, error } = await invokeFunction<{ batch: unknown; records: unknown[] }>('chart-audit', {
    method: 'GET',
    path: `/batches/${batchId}`,
  })
  if (error) throw toApiRequestError(error, 'Failed to load audit batch')
  const d = data as Record<string, unknown>
  return {
    batch: toBatch(d.batch),
    records: (d.records as ChartRecord[]) ?? [],
  }
}

export async function ingestCharts(
  batchId: string,
  charts: Array<{
    sections: Record<string, unknown>
    procedure_type?: string
    procedure_year?: number
    intake_path?: string
  }>,
): Promise<{ ingested: number }> {
  const { data, error } = await invokeFunction<{ ingested: number }>('chart-audit', {
    method: 'POST',
    path: `/batches/${batchId}/ingest`,
    body: { charts },
  })
  if (error) throw toApiRequestError(error, 'Failed to ingest charts')
  return { ingested: (data as Record<string, unknown>)?.ingested as number ?? 0 }
}

export async function processBatch(batchId: string): Promise<{ batch_id: string }> {
  const { data, error } = await invokeFunction<{ batch_id: string }>('chart-audit', {
    method: 'POST',
    path: `/batches/${batchId}/process`,
  })
  if (error) throw toApiRequestError(error, 'Failed to process audit batch')
  return { batch_id: (data as Record<string, unknown>)?.batch_id as string ?? batchId }
}

export async function getBatchReport(batchId: string): Promise<BatchReport> {
  const { data, error } = await invokeFunction<BatchReport>('chart-audit', {
    method: 'GET',
    path: `/batches/${batchId}/report`,
  })
  if (error) throw toApiRequestError(error, 'Failed to load batch report')
  return data as BatchReport
}

export async function getBatchSummary(batchId: string): Promise<BatchSummary> {
  const { data, error } = await invokeFunction<BatchSummary>('chart-audit', {
    method: 'GET',
    path: `/batches/${batchId}/summary`,
  })
  if (error) throw toApiRequestError(error, 'Failed to load batch summary')
  return data as BatchSummary
}

export async function getRuleConfig(facilityId: string): Promise<{ rules: RuleConfigEntry[]; defaults: RuleDefault[] }> {
  const { data, error } = await invokeFunction<{ rules: RuleConfigEntry[]; defaults: RuleDefault[] }>('chart-audit', {
    method: 'GET',
    path: `/config/rules?facility_id=${facilityId}`,
  })
  if (error) throw toApiRequestError(error, 'Failed to load rule configuration')
  const d = data as Record<string, unknown>
  return {
    rules: (d.rules as RuleConfigEntry[]) ?? [],
    defaults: (d.defaults as RuleDefault[]) ?? [],
  }
}

export async function updateRuleConfig(facilityId: string, rules: Array<{ rule_id: string; config: Record<string, unknown> }>): Promise<void> {
  const { error } = await invokeFunction<{ updated: number }>('chart-audit', {
    method: 'PUT',
    path: `/config/rules?facility_id=${facilityId}`,
    body: { rules },
  })
  if (error) throw toApiRequestError(error, 'Failed to update rule configuration')
}

export interface PortfolioEntry {
  facility_id: string
  facility_name: string
  last_audit_date: string | null
  last_batch_score: Record<string, number> | null
  days_since_audit: number | null
  overdue: boolean
  open_findings: number
  schedule: {
    next_due_date: string | null
    cadence: string
    minimum_cadence?: string | null
    reminder_days_before?: number
    last_completed_date?: string | null
  } | null
}

export interface BenchmarkEntry {
  p25: number
  p50: number
  p75: number
  facility_rate: number | null
}

export interface TrendEntry {
  batch_id: string
  period_date: string
  pass_rates: Record<string, number>
  score_distribution: Record<string, number>
  scope_classifications: Record<string, string>
  total_charts: number
  total_findings: number
}

export type QapiFindingStatus = 'open' | 'in_review' | 'pip_created' | 'resolved'
export type QapiFindingSeverity = 'low' | 'medium' | 'high'
export type QapiFindingScope = 'limited' | 'pattern' | 'widespread' | null

export interface QapiFinding {
  id: string
  tenant_id: string
  facility_id: string
  source: 'chart_audit' | 'survey' | 'self_monitoring' | 'manual'
  source_batch_id: string | null
  domain: string
  standard: string
  finding_description: string
  scope_level: QapiFindingScope
  severity: QapiFindingSeverity
  status: QapiFindingStatus
  pip_action_id: string | null
  batch_date: string | null
  chart_token_refs: string[]
  created_by: string | null
  created_at: string
  resolved_at: string | null
  resolved_by: string | null
}

export interface AuditSchedule {
  cadence: 'monthly' | 'quarterly' | 'custom'
  custom_interval_days: number | null
  next_due_date: string | null
  last_completed_date: string | null
  reminder_days_before: number
  minimum_cadence?: 'monthly' | 'quarterly' | null
}

export async function getPortfolio(): Promise<PortfolioEntry[]> {
  const { data, error } = await invokeFunction<{ portfolio: PortfolioEntry[] }>('chart-audit', {
    method: 'GET',
    path: '/portfolio',
  })
  if (error) throw toApiRequestError(error, 'Failed to load chart audit portfolio')
  return ((data as Record<string, unknown>)?.portfolio as PortfolioEntry[]) ?? []
}

/** Redacted portfolio export envelope (#1851 / #836). Admin/editor/superadmin only on backend. */
export interface PortfolioExportEnvelope {
  export_service_version?: string
  redaction_mode: 'redacted' | 'unredacted'
  redaction_applied?: boolean
  raw_identifier_fields_removed?: number
  payload?: unknown
}

export interface PortfolioExportResult {
  export: PortfolioExportEnvelope
  contract_metadata?: Record<string, unknown>
}

export async function exportPortfolio(): Promise<PortfolioExportResult> {
  const { data, error } = await invokeFunction<PortfolioExportResult>('chart-audit', {
    method: 'GET',
    path: '/portfolio/export',
  })
  if (error) throw toApiRequestError(error, 'Failed to export chart audit portfolio')
  if (!data || !asRecord(data.export)) {
    throw new Error('Malformed portfolio export response from server')
  }
  const exportEnvelope = data.export as PortfolioExportEnvelope
  if (exportEnvelope.redaction_mode !== 'redacted' && exportEnvelope.redaction_mode !== 'unredacted') {
    throw new Error('Malformed portfolio export response from server')
  }
  return {
    export: exportEnvelope,
    contract_metadata: asRecord(data.contract_metadata) ? data.contract_metadata : undefined,
  }
}

export function buildPortfolioExportFilename(result: PortfolioExportResult, exportedAt = new Date()): string {
  const day = exportedAt.toISOString().slice(0, 10)
  const mode = result.export.redaction_mode === 'unredacted' ? 'unredacted' : 'redacted'
  return `chart-audit-portfolio-${mode}-${day}.json`
}

/** Trigger browser download of the redacted portfolio export JSON. Returns filename. */
export function triggerPortfolioExportDownload(
  result: PortfolioExportResult,
  exportedAt = new Date(),
): string {
  const filename = buildPortfolioExportFilename(result, exportedAt)
  const blob = new Blob([JSON.stringify(result, null, 2)], {
    type: 'application/json',
  })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.click()
  setTimeout(() => URL.revokeObjectURL(url), 0)
  return filename
}

export async function getBenchmark(facilityId: string): Promise<Record<string, BenchmarkEntry>> {
  const { data, error } = await invokeFunction<{ benchmark: Record<string, BenchmarkEntry> }>('chart-audit', {
    method: 'GET',
    path: `/benchmark?facility_id=${facilityId}`,
  })
  if (error) throw toApiRequestError(error, 'Failed to load chart audit benchmark')
  return ((data as Record<string, unknown>)?.benchmark as Record<string, BenchmarkEntry>) ?? {}
}

export async function getTrends(
  facilityId: string,
  windowDays = 365,
): Promise<{ trends: TrendEntry[]; facility_id: string; window_days: number }> {
  const { data, error } = await invokeFunction<{ trends: TrendEntry[]; facility_id: string; window_days: number }>('chart-audit', {
    method: 'GET',
    path: `/trends?facility_id=${facilityId}&window_days=${windowDays}`,
  })
  if (error) throw toApiRequestError(error, 'Failed to load chart audit trends')
  const payload = (data as Record<string, unknown>) ?? {}
  return {
    trends: (payload.trends as TrendEntry[]) ?? [],
    facility_id: String(payload.facility_id ?? facilityId),
    window_days: Number(payload.window_days ?? windowDays),
  }
}

export async function getSchedule(facilityId: string): Promise<AuditSchedule> {
  const { data, error } = await invokeFunction<{ schedule: AuditSchedule }>('chart-audit', {
    method: 'GET',
    path: `/schedule?facility_id=${facilityId}`,
  })
  if (error) throw toApiRequestError(error, 'Failed to load chart audit schedule')
  return ((data as Record<string, unknown>)?.schedule as AuditSchedule) ?? {
    cadence: 'quarterly',
    custom_interval_days: null,
    next_due_date: null,
    last_completed_date: null,
    reminder_days_before: 7,
    minimum_cadence: null,
  }
}

export async function updateSchedule(facilityId: string, schedule: Partial<AuditSchedule>): Promise<void> {
  const { error } = await invokeFunction<{ updated: boolean }>('chart-audit', {
    method: 'PUT',
    path: `/schedule?facility_id=${facilityId}`,
    body: schedule,
  })
  if (error) throw toApiRequestError(error, 'Failed to update chart audit schedule')
}

export async function listQapiFindings(filters: {
  facilityId?: string
  status?: QapiFindingStatus | 'all'
} = {}): Promise<QapiFinding[]> {
  const params = new URLSearchParams()
  if (filters.facilityId) params.set('facility_id', filters.facilityId)
  if (filters.status && filters.status !== 'all') params.set('status', filters.status)
  const query = params.toString()

  const { data, error } = await invokeFunction<{ findings: QapiFinding[] }>('chart-audit', {
    method: 'GET',
    path: `/qapi/findings${query ? `?${query}` : ''}`,
  })
  if (error) throw toApiRequestError(error, 'Failed to load chart audit QAPI findings')
  return ((data as Record<string, unknown>)?.findings as QapiFinding[]) ?? []
}

export async function updateQapiFindingStatus(
  findingId: string,
  status: QapiFindingStatus,
): Promise<void> {
  const { error } = await invokeFunction<{ updated: boolean }>('chart-audit', {
    method: 'PATCH',
    path: `/qapi/findings/${findingId}`,
    body: { status },
  })
  if (error) throw toApiRequestError(error, 'Failed to update QAPI finding')
}

export async function createQapiFindingPip(
  findingId: string,
): Promise<{ pip_action_id: string; finding_id: string; due_date: string; visit_id: string }> {
  const { data, error } = await invokeFunction<{
    pip_action_id: string
    finding_id: string
    due_date: string
    visit_id: string
  }>('chart-audit', {
    method: 'POST',
    path: `/qapi/findings/${findingId}/pip`,
  })
  if (error) throw toApiRequestError(error, 'Failed to create PIP for QAPI finding')
  return data as { pip_action_id: string; finding_id: string; due_date: string; visit_id: string }
}
